// KeyPointVerticalNumericComponent.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import { headingLetterSpacing } from '../../../utils/headingTypography';
import Image from '../../Image/Image';

// Cloudflare SVG
const KEYPOINT_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/KeyPointVerticalVector.svg';

const KeyPointVerticalNumericComponent = ({ data }) => {
  const { title, description, descriptionLink, footer, footerLink, steps } =
    data;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ position: 'relative', width: '1100px', display: 'flex' }}>
        {/* Sticky Blue Box */}
        <Box
          sx={{
            position: 'sticky',
            top: '120px',
            width: '390px',
            minHeight: '476px',
            backgroundColor: '#162F3B',
            borderRadius: '64px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            boxSizing: 'border-box',
            padding: '40px 24px 40px 24px',
            zIndex: 2,
            flexShrink: 0,
            mt: '40px',
            mb: '40px',
            marginRight: '-80px',
            alignSelf: 'flex-start',
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.5rem',
              letterSpacing: headingLetterSpacing('3rem'),
              color: '#FFFFFF',
              mb: '32px',
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
          >
            {title}
          </Typography>

          <LinkWithIconComponent
            href={descriptionLink || undefined}
            component={descriptionLink ? 'a' : 'p'}
            showIcon={Boolean(descriptionLink)}
            iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
            iconSize="1rem"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#FFFFFF',
              mb: '32px',
              textAlign: 'center',
            }}
          >
            {description}
          </LinkWithIconComponent>

          <LinkWithIconComponent
            href={footerLink || undefined}
            iconSrc="https://assets.askhippo.co.za/Icons/common/Union.svg"
            iconSize="0.75rem"
            sx={{
              mb: '15px',
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
            }}
          >
            {footer}
          </LinkWithIconComponent>
        </Box>

        {/* Right White Panel */}
        <Box
          sx={{
            width: '700px',
            backgroundColor: '#FFFFFF',
            borderRadius: '48px',
            paddingTop: '40px',
            paddingRight: '80px',
            paddingLeft: '104px',
            paddingBottom: '40px',
            boxSizing: 'border-box',
          }}
        >
          {steps.map(({ title, text }, index) => (
            <ScrollFadeWrapper key={index} delay={index * 0.08}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mt: index === 0 ? 0 : '16px',
                }}
              >
                {/* Number Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '120px',
                      height: '120px',
                    }}
                  >
                    <Image
                      width={120}
                      height={120}
                      src={KEYPOINT_VECTOR_URL}
                      alt="Key point illustration (vertical)"
                      sx={{ width: '100%', height: '100%' }}
                    />

                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontFamily: 'Pally, Inter, sans-serif',
                        letterSpacing: '-0.03em',
                        fontWeight: 700,
                        fontSize: '4.5rem',
                        textAlign: 'center',
                        color: '#162F3B',
                        pointerEvents: 'none',
                      }}
                    >
                      {index + 1}
                    </Typography>
                  </Box>

                  {index !== steps.length - 1 && (
                    <Box
                      sx={{
                        width: '8px',
                        height: '50px',
                        backgroundColor: '#E3E6E8',
                        borderRadius: '100px',
                        mt: '16px',
                      }}
                    />
                  )}
                </Box>

                {/* Step Content */}
                <Box sx={{ maxWidth: '356px', textAlign: 'left', ml: '40px' }}>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      lineHeight: '1.75rem',
                      letterSpacing: headingLetterSpacing('1.5rem'),
                      color: '#162F3B',
                      mb: '32px',
                    }}
                  >
                    {title}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              </Box>
            </ScrollFadeWrapper>
          ))}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default KeyPointVerticalNumericComponent;
